import { endpoint } from "./endpoints";
import { formatDateTime } from "./formatDateTime";

export const ACCOUNTANT_API =
  process.env.REACT_APP_ACCOUNTANT_API;

export const DRIVER_LEDGER_CSV_LOG_PATH = (driverId) =>
  `/salary-disbursement/salary-and-awards-detail?driver_id=${encodeURIComponent(
    driverId
  )}&tab=logCsv`;

export const parseBody = async (response) => {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return { rawMessage: text };
  }
};

export const getErrorMessage = (payload, response) => {
  if (!payload || typeof payload !== "object") {
    return response?.statusText || "Something went wrong. Please try again.";
  }
  return (
    payload.errorMessage ||
    payload.message ||
    payload.error ||
    (Array.isArray(payload.errors) ? payload.errors.join(", ") : null) ||
    (response?.status ? `Request failed (${response.status})` : "Request failed.")
  );
};

export const extractArrayFromData = (data, preferredKeys) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (typeof data !== "object") return [];
  for (const k of preferredKeys) {
    if (Array.isArray(data[k])) return data[k];
  }
  return [];
};

export const formatCsvLogStatus = (item) => {
  if (item == null || typeof item !== "object") return "—";
  const status = item.status;
  if (status === 0 || status === "0") return "Pending";
  if (status === 1 || status === "1") return "Completed";
  if (status === 2 || status === "2" || status === 3 || status === "3") return "Failed";

  const raw =
    item.generate_file ??
    item.generateFile ??
    item.generated_file ??
    item.file_status ??
    item.generation_status;
  if (raw == null || raw === "") return "Pending";
  if (typeof raw === "boolean") return raw ? "Completed" : "Pending";
  const text = String(raw).trim();
  if (!text) return "Pending";
  if (/^(true|1|generated|complete|completed|success)$/i.test(text)) return "Completed";
  if (/^(false|0|pending|processing|in[_\s-]?progress)$/i.test(text)) return "Pending";
  if (/fail/i.test(text)) return "Failed";
  return text;
};

/** @deprecated use formatCsvLogStatus */
export const formatCsvLogGenerateFile = formatCsvLogStatus;

export const getCsvLogStatusCode = (item) => {
  if (item == null || typeof item !== "object") return null;
  if (item.status == null || item.status === "") return null;
  const n = Number(item.status);
  return Number.isNaN(n) ? null : n;
};

export const isCsvLogDownloadReady = (row) => {
  const status = row?.statusCode ?? getCsvLogStatusCode(row);
  return status === 1 && Boolean(String(row?.fileUrl ?? "").trim());
};

export const formatCsvLogDateTime = (iso) => formatDateTime(iso);

export const mapCsvLogRow = (item, index) => {
  const csvLink = String(
    item?.csv_link ?? item?.csvLink ?? item?.file_url ?? item?.csv_url ?? item?.file_path ?? ""
  ).trim();
  const filter = item?.filter && typeof item.filter === "object" ? item.filter : null;
  const search = String(filter?.search ?? "").trim();
  const type = String(filter?.type ?? "").trim();
  const fromDate = String(filter?.from_date ?? filter?.fromDate ?? "").trim();
  const toDate = String(filter?.to_date ?? filter?.toDate ?? "").trim();
  const filterParts = [];
  if (type) filterParts.push(`Transition Type: ${type}`);
  if (search) filterParts.push(`Search: ${search}`);
  if (fromDate && toDate) filterParts.push(`Date: ${fromDate} to ${toDate}`);

  return {
    id: item?._id ?? item?.id ?? item?.csv_log_id ?? `csv-log-${index}`,
    fileName:
      String(item?.file_name ?? "").trim() ||
      (csvLink ? csvLinkToFileName(csvLink) : "—"),
    createdAt: formatCsvLogDateTime(item?.createdAt ?? item?.created_at),
    statusCode: getCsvLogStatusCode(item),
    statusLabel: formatCsvLogStatus(item),
    generateFile: formatCsvLogStatus(item),
    fileUrl: csvLink,
    reportType: item?.report_type ?? item?.type ?? "—",
    filters: filterParts.length ? filterParts.join(" | ") : "—",
  };
};

export const resolveFileUrl = (url) => {
  const raw = String(url ?? "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  return `${ACCOUNTANT_API}${raw.startsWith("/") ? raw : `/${raw}`}`;
};

export const isExternalCsvFileUrl = (url) => {
  const resolved = resolveFileUrl(url);
  if (!resolved) return false;
  if (!/^https?:\/\//i.test(resolved)) return false;
  const apiOrigin = ACCOUNTANT_API.replace(/\/v\d+\/?$/, "").replace(/\/$/, "");
  return !resolved.startsWith(apiOrigin);
};

export const downloadCsvLogFile = async (row) => {
  if (!row?.fileUrl) {
    throw new Error("File is not available for download yet.");
  }

  const fileUrl = resolveFileUrl(row.fileUrl);
  const filename =
    row.fileName && row.fileName !== "—" ? row.fileName : `driver-ledger-${row.id}.csv`;

  if (isExternalCsvFileUrl(fileUrl)) {
    try {
      const response = await fetch(fileUrl, { method: "GET" });
      if (response.ok) {
        const blob = await response.blob();
        return { blob, filename };
      }
    } catch {
      /* fall through to open in new tab */
    }
    window.open(fileUrl, "_blank", "noopener,noreferrer");
    return null;
  }

  const token = localStorage.getItem("sessionToken");
  if (!token) {
    throw new Error("Session expired. Please log in again.");
  }

  const response = await fetch(fileUrl, {
    method: "GET",
    headers: { Authorization: `Bearer ${token}` },
  });
  const payload = await parseBody(response);

  if (!response.ok) {
    throw new Error(getErrorMessage(payload, response));
  }

  const blob = await response.blob();
  return { blob, filename };
};

export const fetchDriverLedgerCsvLog = async (driverId, token) => {
  const params = new URLSearchParams({ driver_id: driverId });
  const url = `${ACCOUNTANT_API}${endpoint.getDriverLedgerCsvLog}?${params.toString()}`;

  const response = await fetch(url, {
    method: "GET",
    headers: { Authorization: `Bearer ${token}` },
  });
  const payload = await parseBody(response);

  if (!response.ok) {
    throw new Error(getErrorMessage(payload, response));
  }

  const apiCode = payload?.code;
  if (apiCode != null && apiCode !== 200 && apiCode !== 201) {
    throw new Error(getErrorMessage(payload, response));
  }

  const raw = Array.isArray(payload)
    ? payload
    : extractArrayFromData(payload?.data, [
        "driver_ledger_csv_log_data",
        "csv_log_data",
        "ledger_csv_log_data",
        "report_data",
        "csv_logs",
        "logs",
        "data",
        "list",
      ]);

  return raw.map(mapCsvLogRow);
};

export const requestDriverLedgerCsvExport = async ({
  driverId,
  fromDate,
  toDate,
  type,
  token,
}) => {
  const params = new URLSearchParams({
    driver_id: driverId,
  });
  if (fromDate && toDate) {
    params.set("from_date", fromDate);
    params.set("to_date", toDate);
  }
  if (type && String(type).trim()) {
    params.set("type", String(type).trim());
  }
  const url = `${ACCOUNTANT_API}${endpoint.getDriverLedgerCSV}?${params.toString()}`;

  const response = await fetch(url, {
    method: "GET",
    headers: { Authorization: `Bearer ${token}` },
  });
  const payload = await parseBody(response);

  if (!response.ok) {
    throw new Error(getErrorMessage(payload, response));
  }

  const apiCode = payload?.code;
  if (apiCode != null && apiCode !== 200 && apiCode !== 201) {
    throw new Error(getErrorMessage(payload, response));
  }

  return payload;
};

/** Export finished synchronously — csv_link present in API response */
export { hasCsvLinkInResponse as isImmediateDriverLedgerCsvResponse } from "./csvExportSocket";

export const extractCsvLinkFromExportResponse = (payload) => {
  if (!payload || typeof payload !== "object") return "";
  const data = payload.data;
  if (!data || typeof data !== "object") return "";
  return String(data.csv_link ?? data.csvLink ?? data.file_url ?? data.file_path ?? "").trim();
};

export const csvLinkToFileName = (csvLink) => {
  const raw = String(csvLink ?? "").trim();
  if (!raw) return "driver-ledger.csv";
  const pathPart = raw.split("?")[0];
  const segment = pathPart.split("/").filter(Boolean).pop();
  return segment || "driver-ledger.csv";
};
